import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import BtButton from '../bt-button/bt-button';
import { serviceEvaluations, SupplierSummary } from '../../services/service-evaluations';
import { EvaluationErrors } from '../../util/evaluationContract';
import { readEvaluations } from '../../util/evaluationsCache';
import { formatScore } from '../../util/evaluationFormat';
import { TAG_ARCHIVED_LABEL } from '../../util/evaluationStatusMeta';
import { errorKeyOf } from '../app-evaluation-start/evaluationStart';
import CommentList from '../page-supplier-evaluations/CommentList';
import ReadOnlyStars from '../page-supplier-evaluations/ReadOnlyStars';
import ResponseModal from '../page-supplier-evaluations/ResponseModal';
import ScoreBarList, { ScoreBarRow } from '../page-supplier-evaluations/ScoreBarList';
import Trend from '../page-supplier-evaluations/Trend';
import { formatShortDay, formatStockholmDay } from '../page-supplier-evaluations/reviewFormat';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import styles from './SupplierEvaluationSummary.module.scss';

type Props = {
  supplierId: string;
  /** A selection flow must not be left or change anything: no buttons, no Dölj svar. */
  readOnly: boolean;
  onStartEvaluation?: () => void;
  onShowAll?: () => void;
  onAnswerHidden?: (summary: SupplierSummary) => void;
};

type LoadFailure = 'migrationPending' | 'failed';

// The register's section title, as its Allmänt tab draws Leverantörsprofil.
const boxTitle = (title: ReactNode) => (
  <div className="detail-sec-title no-border">
    <span className="detail-sec-title-title">{title}</span>
  </div>
);

const byAnswersThenName = (
  a: SupplierSummary['byTag'][number],
  b: SupplierSummary['byTag'][number],
) => b.count - a.count || a.name.localeCompare(b.name, 'sv');

const SupplierEvaluationSummary: FC<Props> = ({
  supplierId,
  readOnly,
  onStartEvaluation,
  onShowAll,
  onAnswerHidden,
}) => {
  const [t, i18n] = useTranslation('evaluations');
  const [summary, setSummary] = useState<SupplierSummary | null>(null);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [openResponseId, setOpenResponseId] = useState<string | null>(null);

  // The cached summary shows at once; a failed refresh behind it stays silent.
  const load = useCallback(
    async ({ skipCache = false } = {}) => {
      const read = readEvaluations(
        'summary',
        supplierId,
        () => serviceEvaluations.getSupplierSummary(supplierId),
        { skipCache },
      );
      if (read.cached) {
        setSummary(read.cached);
        setFailure(null);
      }
      try {
        const { value, changed } = await read.fresh;
        if (changed) setSummary(value);
        setFailure(null);
        return value;
      } catch (error) {
        if (read.cached) return read.cached;
        if (errorKeyOf(error) === EvaluationErrors.MigrationPending) {
          setFailure('migrationPending');
          return null;
        }
        reportUnexpected(error);
        setFailure('failed');
        return null;
      }
    },
    [supplierId],
  );

  const onHidden = async () => {
    const refreshed = await load({ skipCache: true });
    if (refreshed) onAnswerHidden?.(refreshed);
  };

  useEffect(() => {
    load();
  }, [load]);

  const retry = () => {
    setFailure(null);
    setSummary(null);
    load();
  };

  const shortDay = (instant: string) => formatShortDay(formatStockholmDay(instant), i18n.language);

  if (failure === 'migrationPending') {
    return (
      <p className={styles.muted}>
        {t('errors.migrationPending', 'Utvärderingar förbereds. Försök igen om en stund.')}
      </p>
    );
  }
  if (failure) {
    return (
      <div className={styles.loadError} role="alert">
        <span>{t('review.loadFailed', 'Utvärderingarna kunde inte hämtas.')}</span>
        <BtButton color="white" size="xxs" tabIndex={0} onClick={retry}>
          {t('review.retry', 'Försök igen')}
        </BtButton>
      </div>
    );
  }
  if (!summary) {
    return <p className={styles.muted}>{t('panel.loading', 'Hämtar utvärderingar…')}</p>;
  }

  const rated = summary.evaluationCount > 0 || summary.comments.length > 0;
  const startEvaluation = readOnly ? undefined : onStartEvaluation;
  const showAll = readOnly ? undefined : onShowAll;

  const scoreHead = (
    <div className={styles.cardHead}>
      {boxTitle(t('panel.score.label', 'Ert betyg'))}
      {(showAll || startEvaluation) && (
        <div className={styles.actions}>
          {showAll && (
            <BtButton size="small" color="white" tabIndex={0} onClick={() => showAll()}>
              {t('panel.viewAll', 'Visa alla utvärderingar')}
            </BtButton>
          )}
          {startEvaluation && (
            <BtButton size="small" tabIndex={0} onClick={() => startEvaluation()}>
              {t('register.newEvaluation', 'Ny utvärdering')}
            </BtButton>
          )}
        </div>
      )}
    </div>
  );

  if (!rated) {
    return (
      <div className={styles.summary}>
        <BoxHolder className={styles.box}>
          {scoreHead}
          {summary.requests.length > 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>{t('supplierScore.notRated', 'Inte betygsatt')}</p>
              <p className={styles.muted}>
                {t(
                  'review.empty.description',
                  'När någon har svarat visas betyg per tagg och kommentarer här.',
                )}
              </p>
            </div>
          ) : (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>{t('panel.empty.title', 'Inga utvärderingar än')}</p>
              <p className={styles.muted}>
                {t(
                  'panel.empty.text',
                  'Utvärdera leverantören efter ett projekt, eller för arbete som gjordes innan den fanns i Accurator.',
                )}
              </p>
            </div>
          )}
        </BoxHolder>
      </div>
    );
  }

  const tagRows: ScoreBarRow[] = [
    ...[...summary.byTag].sort(byAnswersThenName).map(tag => ({
      key: tag.tagId,
      label: tag.archived
        ? `${tag.name} · ${t(TAG_ARCHIVED_LABEL.labelKey, TAG_ARCHIVED_LABEL.fallback)}`
        : tag.name,
      score: tag.score,
      count: tag.count,
    })),
    ...(summary.untagged.count
      ? [
          {
            key: 'untagged',
            label: t('review.tags.untagged', 'Utan tagg'),
            score: summary.untagged.score,
            count: summary.untagged.count,
          },
        ]
      : []),
  ];

  return (
    <div className={styles.summary}>
      <BoxHolder className={`${styles.box} ${styles.hero}`}>
        {scoreHead}
        <div className={styles.score}>
          {summary.score !== null ? (
            <div className={styles.scoreRow}>
              <span className={styles.scoreNumber}>{formatScore(summary.score)}</span>
              <ReadOnlyStars rating={summary.score} size={18} />
            </div>
          ) : (
            <span className={styles.muted}>{t('supplierScore.notRated', 'Inte betygsatt')}</span>
          )}
          {summary.evaluationCount > 0 && summary.lastEvaluatedAt && (
            <span className={styles.muted}>
              {[
                t('panel.score.answers', {
                  count: summary.evaluationCount,
                  defaultValue_one: '{{count}} svar',
                  defaultValue_other: '{{count}} svar',
                }),
                t('panel.score.projects', {
                  count: summary.evaluatedProjectsCount,
                  defaultValue_one: '{{count}} projekt',
                  defaultValue_other: '{{count}} projekt',
                }),
              ].join(' · ')}
              {' · '}
              <span className={styles.nowrap}>
                {t('panel.score.latest', 'senaste svar {{date}}', {
                  date: shortDay(summary.lastEvaluatedAt),
                })}
              </span>
            </span>
          )}
          <span className={`${styles.muted} ${styles.small}`}>
            {t('review.score.explainer', 'Snittet av varje svars medelvärde. Vet ej räknas inte.')}
          </span>
        </div>
        <div className={styles.trend}>
          <Trend points={summary.trend} formatDay={shortDay} />
        </div>
      </BoxHolder>
      <div className={styles.pair} data-summary-pair>
        <BoxHolder className={styles.box}>
          {boxTitle(t('review.comments.title', 'Kommentarer'))}
          <CommentList
            comments={summary.comments}
            onShowAnswer={setOpenResponseId}
            formatDay={shortDay}
          />
        </BoxHolder>
        <BoxHolder className={styles.box}>
          {boxTitle(t('panel.tags.title', 'Per tagg'))}
          {summary.byTag.length > 0 ? (
            <ScoreBarList rows={tagRows} />
          ) : (
            <p className={styles.muted}>
              {t(
                'panel.tags.empty',
                'Här visas betyget per tagg när en besvarad utvärdering har taggar.',
              )}
            </p>
          )}
        </BoxHolder>
      </div>
      {openResponseId && (
        <ResponseModal
          responseId={openResponseId}
          onClose={() => setOpenResponseId(null)}
          onHidden={onHidden}
          allowHide={!readOnly}
        />
      )}
    </div>
  );
};

export default SupplierEvaluationSummary;
